import { toErrorEnvelope } from '@crowdfpl/domain/error-envelope';
export class ApiHttpError extends Error {
    status;
    code;
    isAuthError;
    details;
    constructor(message, init) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = 'ApiHttpError';
        this.status = init?.status;
        this.code = init?.code;
        this.isAuthError = init?.isAuthError;
        this.details = init?.details;
    }
}
export async function readResponseBody(response) {
    const text = await response.text();
    if (!text) {
        return { json: null, text: null };
    }
    try {
        return { json: JSON.parse(text), text };
    }
    catch {
        return { json: null, text };
    }
}
function messageFromText(text, url, status) {
    if (!text) {
        return status === 401 ? 'Unauthorized' : `Failed to fetch ${url}`;
    }
    const trimmed = text.trim();
    if (trimmed.length > 0 && trimmed.length < 500) {
        return trimmed;
    }
    return status === 401 ? 'Unauthorized' : `Failed to fetch ${url}`;
}
function envelopeFromBody(status, body, url) {
    const { json, text } = body;
    if (json?.status && json.code && json.message) {
        return toErrorEnvelope({
            status: json.status,
            code: json.code,
            message: json.message,
            meta: json.details,
        });
    }
    if (json?.error?.message || json?.error?.code) {
        return toErrorEnvelope({
            status,
            code: json.error.code ?? 'UNKNOWN',
            message: json.error.message ?? messageFromText(text, url, status),
            meta: json.error.details,
        });
    }
    if (json?.message || json?.code) {
        return toErrorEnvelope({
            status,
            code: json.code ?? 'UNKNOWN',
            message: json.message ?? messageFromText(text, url, status),
            meta: json.details,
        });
    }
    return toErrorEnvelope({
        status,
        code: 'UNKNOWN',
        message: messageFromText(text, url, status),
    });
}
function toApiHttpError(envelope, isAuthError) {
    return new ApiHttpError(envelope.message, {
        status: envelope.status,
        code: envelope.code,
        isAuthError,
        details: envelope.details,
    });
}
export async function parseErrorResponse(response, url) {
    const body = await readResponseBody(response);
    const envelope = envelopeFromBody(response.status, body, url);
    const isAuthError = response.status === 401 ||
        /invalid\s+access\s+token/i.test(envelope.message) ||
        envelope.code.toUpperCase() === 'INVALID_ACCESS_TOKEN';
    return toApiHttpError(envelope, isAuthError);
}
export async function handleResponse(response, url) {
    if (!response.ok) {
        throw await parseErrorResponse(response, url);
    }
    if (response.status === 204 || response.status === 205) {
        return null;
    }
    const body = await readResponseBody(response);
    if (body.json !== null) {
        return body.json;
    }
    throw new ApiHttpError(`Expected JSON response from ${url}`, { status: response.status });
}
